import React from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Soft-warning modal shown right before an enroll / transfer / renewal /
 * bulk-reassign operation that would push the destination batch at or above
 * its configured capacity.
 *
 * `batchName`     — friendly label for the destination batch.
 * `capacity`      — configured maximum (integer).
 * `enrolled`      — current active enrollment count.
 * `actionLabel`   — verb shown on the confirm button (default "Proceed Anyway").
 * `description`   — extra context the caller wants to show (e.g. how many
 *                   students will be moved on bulk reassign).
 *
 * Returns the standard shadcn dialog open/close API.
 */
export function CapacityWarningDialog({
  open,
  onOpenChange,
  batchName,
  capacity,
  enrolled,
  actionLabel = "Proceed Anyway",
  description,
  onConfirm,
}) {
  const over = enrolled > capacity;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="capacity-warning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" />
            Batch capacity reached
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2 text-sm text-slate-700">
          <p>
            <strong>{batchName || "This batch"}</strong> has{" "}
            {over ? "exceeded" : "reached"} its maximum capacity.
          </p>
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 grid grid-cols-2 gap-y-1">
            <span className="text-slate-500">Capacity</span>
            <span className="text-right font-medium text-slate-900" data-testid="capacity-warning-capacity">
              {capacity ?? "—"}
            </span>
            <span className="text-slate-500">Currently Enrolled</span>
            <span className={`text-right font-semibold ${over ? "text-red-600" : "text-amber-700"}`} data-testid="capacity-warning-enrolled">
              {enrolled ?? "—"}
            </span>
          </div>
          {description && (
            <p className="text-xs text-slate-500">{description}</p>
          )}
          <p className="text-sm text-slate-600">Do you still want to proceed?</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="capacity-warning-cancel"
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            onClick={() => { onOpenChange(false); onConfirm?.(); }}
            data-testid="capacity-warning-confirm"
            className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
          >
            {actionLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CapacityWarningDialog;
